import { Vector3 } from 'three'
import { BoxSizeEnum } from '../core/enums'
import type { DreamteckSpline } from '../core/spline/dreamteckSpline'
import type { RoadSpline } from '../core/spline/splineGenerator'
import type { EntryPath } from './entryPath'
import type { ExitPath } from './exitPath'
import type { SampledPath } from './sampledPath'
import { BUS_PARKED_MIDDLE_SPAN } from './tuning'

export interface PassengerTuning {
  passengerSideOffset: number
  /** Per bus prefab — `Bus.passengerHeadExtension` is a field on the Bus. */
  passengerHeadExtension: Readonly<Record<BoxSizeEnum, number>>
  passengerTailExtension: Readonly<Record<BoxSizeEnum, number>>
}

/**
 * Port of the parts of `BusSplineFollower` (TypeDefIndex 8566) that gameplay
 * depends on.
 *
 * The bus occupies a **span** of the road, from `tailPercent` to `headPercent`,
 * not a point. `QueueController.FindBusAtStop` asks each active bus
 * `CoversMainSplinePercent(stopPercent)`, so a bus is "at" a stop whenever its
 * body is over it.
 *
 * The road is driven as a **loop**, which is the mechanic the port was missing.
 * From `TravelForwardWrapped`:
 *
 *     remaining = spline.CalculateLength(from, 1.0)
 *     if (spline.isClosed || distance <= remaining)
 *         return spline.Travel(from, distance, Forward)
 *     else
 *         return spline.Travel(0, distance - remaining, Forward)
 *
 * So on an open spline a bus that runs off the end reappears at the start —
 * the tunnel mouths at either end (`SplineGenerator.GenereteCovers`,
 * `ConveyorCover.prefab`) are what the player sees it drive through. A bus
 * circulates until it is full; only then does `DepartAndExit` run.
 */

export type FollowerState = 'Entering' | 'OnMainRoad' | 'Exiting'

/** `roadEndsReached`: the body reaches into the stub before the drawn road. */
export const ROAD_END_START = 1
/** The body reaches into the stub past the end of the drawn road. */
export const ROAD_END_END = 2

export class BusSplineFollower {
  state: FollowerState = 'Entering'

  /** Arc distance of the nose from the start of the road. */
  headDistance = 0
  /** Body length in world units. */
  readonly bodyLength: number

  /** `IsHeldAtStop` — a waiting area is loading this bus. */
  isHeldAtStop = false
  /** `IsDeparting` — running out on the exit spline, no longer wrapping. */
  isDeparting = false

  /**
   * `Behaviour.enabled` — Unity's own switch, and the only thing that stops a
   * follower moving.
   *
   * 🔍 `BusSplineFollower$$PlayFailCrash__0x21135b4.c`:103 —
   * `UnityEngine.Behaviour.set_enabled(this, false)`, immediately before
   * `PauseAccommodationAnimation` and `ClearTrails`. Unity then skips this
   * component's `LateUpdate` entirely, so a bus that has rolled onto its side
   * stops dead where it fell.
   *
   * The port's `advanceRoadTraffic` has to test it by hand: nothing else here
   * models the MonoBehaviour lifecycle, so an unenforced flag would be a
   * comment pretending to be code — which is exactly what this replaces.
   */
  enabled = true

  /** How far past the end of the road the bus has run while exiting. */
  exitDistance = 0

  /**
   * `EqualizeSpeedMultiplier` (0x4C) — the last factor in the speed product.
   *
   * 🔍 `BusSplineFollower.LateUpdate`:323 (`__0x210cc5c.c`):
   *
   *     step = baseSpeed * entryMultiplier
   *          * this[0x9c]   // ComputeTrafficSpeedFactor's result
   *          * this[0x4c];  // <- this field
   *
   * Written **only** by `SplineController.Update`, once per frame for every bus,
   * and reset to exactly 1.0 on every path that does not run the spacing loop —
   * so a bus never keeps a stale multiplier. See `Session.updateEqualizeSpeed`.
   */
  equalizeSpeedMultiplier = 1

  /**
   * `_isFullyGrown` — is the whole body clear of the start of the curve?
   *
   * 🔍 `LateUpdate__0x210cc5c.c`:341-365 — the tail is **pinned** until it is:
   *
   *     if (!_isFullyGrown) {
   *         arc = _tailPercent <= _headPercent
   *             ? CalculateLength(_tailPercent, _headPercent)
   *             : CalculateLength(_tailPercent, 1.0) + CalculateLength(0, _headPercent);
   *         if (_busLengthWorldUnits <= arc) _isFullyGrown = true;
   *     } else {
   *         _tailPercent = TravelBackwardWrapped(_headPercent, _busLengthWorldUnits);
   *     }
   *
   * The arc is measured from the **pinned tail** to the advancing head, and the
   * threshold is `_busLengthWorldUnits` (0x98) — the body's span **on the road**,
   * a percent of the loop. `Initialize__0x2110984.c`:91 writes
   * `*(this + 0xa0) = 0`, so it starts false.
   *
   * So the bus really does unfold: the tail stays where it was parked while the
   * nose pulls away, and the drawn span stretches from the parked chord up to
   * the road span. For a Large bus that is 14.07 → 22.56 units — the ribbed
   * middle roughly doubles, which is what the `BusAccommodation` rings are
   * unfolding *into* as they switch off one by one.
   *
   * ⚠️ The old note here said this was "emphatically *not* what makes the bus
   * appear to lengthen — that is the tail cap". **Wrong**, and it cost a visible
   * bug: the caps are 3.19–6.09 units, while the stretch the decomp describes is
   * the middle body, up to ~8.5 units on a Large. With `growLength` capped at
   * the parked chord the flag was true on frame one and the unfolding never
   * happened at all, so the rings vanished off a body that never grew.
   */
  isFullyGrown = false

  /**
   * The throwaway merge curve, while `state === 'Entering'`. Distances are
   * measured along it instead of along the road until the tail clears the join.
   */
  entryPath: EntryPath | null = null

  /** The run-off curve, while `state === 'Exiting'`. */
  exitPath: ExitPath | null = null
  /** Where on the road the bus was when it left it. */
  private roadDistanceAtDepart = 0
  /** Arc distance of the drawn road's two ends, cached on first use. */
  private drawnStart = -1
  private drawnEnd = -1

  constructor(
    private road: RoadSpline,
    bodyLength: number,
    /**
     * Which prefab this bus came from.
     *
     * `_bus` is a field on the original follower and every passenger-extension
     * read goes through it; carrying the size is the same reach with none of
     * the coupling.
     */
    readonly size: BoxSizeEnum,
    /** Arc distance where this bus merged onto the road. */
    entryDistance: number,
    entryPath: EntryPath | null = null,
    /**
     * How far along the entry curve the **drawn tail** is pinned while the bus
     * unfolds — `busParkedTailArc(size)`, 2.93 / 3.88 / 5.88.
     *
     * 🔍 `Initialize__0x2110984.c`:413-443 projects `_bus.tailPart` onto the
     * curve and leaves `_tailPercent` there; the curve itself starts further
     * back, at `Bus.tailEndPoint`. Everything about the unfolding is measured
     * from this arc, not from the curve's origin.
     *
     * ⚠️ **This parameter used to be `entryHeadStart`, and it was the bug.** It
     * was passed `bus.endPointSpan` (8.08 / 10.06 / 14.07) and the tail was
     * treated as sitting at arc 0, so the bus was spawned already unfolded —
     * see `isFullyGrown`.
     */
    parkedTailArc = 0,
  ) {
    this.bodyLength = bodyLength
    this.parkedTailArc = parkedTailArc
    // 🔍 The threshold is `_busLengthWorldUnits` flat — the middle's span on the
    // road (`LateUpdate`:356 compares it against the tail-to-head arc).
    this.growLength = bodyLength
    if (entryPath) {
      this.entryPath = entryPath
      this.state = 'Entering'
      // 🔍 `Initialize`:419-443 — the nose starts exactly `BUS_PARKED_MIDDLE_SPAN`
      // ahead of the pinned tail, which is the parked footprint: nose cap, two
      // units of middle, tail cap. It does **not** start a body length ahead.
      this.headDistance = Math.min(parkedTailArc + BUS_PARKED_MIDDLE_SPAN, entryPath.length)
      this.isFullyGrown = this.drawnMiddleSpan >= this.growLength
    } else {
      this.state = 'OnMainRoad'
      this.headDistance = entryDistance
      this.isFullyGrown = true
    }
  }

  /** How far the nose must travel before the whole body is clear of the start. */
  private readonly growLength: number

  /** Arc of the pinned drawn tail on the entry curve; 0 once on the road. */
  private readonly parkedTailArc: number

  /**
   * How much middle is drawn right now — the arc from the pinned tail to the
   * nose, which is exactly what `LateUpdate`:341-356 compares against
   * `_busLengthWorldUnits`.
   */
  get drawnMiddleSpan(): number {
    return this.headDistance - this.parkedTailArc
  }

  /**
   * Head-to-tail span. Fixed at `bodyLength` except in the corner the flag
   * guards, where the entry curve is shorter than the bus.
   */
  get grownBodyLength(): number {
    if (this.isFullyGrown) return this.bodyLength
    // 🔍 From the **pinned tail**, not from the curve's origin. Measuring from 0
    // handed a bus that spawns 3-6 units in a middle that was already full, so
    // nothing unfolded — `npm run check:busgrow` on level 1 read
    // "drawn 14.20 -> 14.20, nose travelled 0.00".
    return Math.max(0, Math.min(this.bodyLength, this.drawnMiddleSpan))
  }

  /**
   * How much of the tail cap has cleared the start of the curve.
   *
   * `UpdateCapBodies` clips it against zero —
   *
   *     clipFrom = max(0, tailPercent - tailCapWorldLength / splineLength)
   *     clipTo   = tailPercent
   *
   * — so while the bus is still coming out of the lot its drawn tail is pinned
   * at the entry point and the cap opens up behind it as the rest drives on.
   * That is what reads as the bus lengthening: nose and body move, tail does
   * not. Returns the full cap once there is room, and 0 with none.
   */
  tailCapRoom(capLength: number): number {
    const path = this.currentPath
    if (!path) return capLength
    const tail = this.headDistance - this.grownBodyLength
    return Math.max(0, Math.min(capLength, tail))
  }

  /** Where this bus is drawn from — the merge curve, or the road. */
  get isOnEntryPath(): boolean {
    return this.state === 'Entering' && this.entryPath !== null
  }

  /**
   * Port of `CheckStateTransition`:
   *
   *     if (State != Entering) return
   *     if (_tailPercent < GetPointPercent(_entryPointIndex) && _headPercent < 0.985) return
   *     TransitionToMainRoad()
   *
   * i.e. hand over once the tail has passed the join, or the nose has almost
   * run out of entry curve. Distance along the entry curve past the join equals
   * distance along the road, so the handover is an offset.
   */
  checkStateTransition(): void {
    const path = this.entryPath
    if (this.state !== 'Entering' || !path) return

    const tail = this.headDistance - this.bodyLength
    const nearlyDone = this.headDistance >= path.length * 0.985
    if (tail < path.joinDistance && !nearlyDone) return

    // 🔍 `TransitionToMainRoad` (`__0x2112084.c`, inlined here) hands over by
    // **projecting a world position**, not by arc arithmetic:
    //
    //     tailPos = _currentSpline.Evaluate(_tailPercent).position      // :80
    //     headPos = _currentSpline.Evaluate(_headPercent).position      // :85
    //     tailProj = _mainSpline.Project(tailPos).percent               // :87
    //     headProj = _mainSpline.Project(headPos).percent               // :90
    //     ...
    //     if (_isFullyGrown) {                                          // :143
    //         _tailPercent = tailProj
    //         _headPercent = TravelForwardWrapped(tailProj, _busLengthWorldUnits)
    //     } else {
    //         _tailPercent = MainSplineEntryPercent
    //         _headPercent = headProj
    //     }
    //
    // Projection matters because the entry curve's tail is a Catmull-Rom through
    // resampled road points while the road itself is a B-spline — their arc
    // lengths over the same stretch do not agree, so `roadJoin + (head − join)`
    // lands the bus a little off the curve and it visibly jumps and re-aims.
    //
    // ⚠️ The **tail** is the anchor, and the head is rebuilt from it. `headProj`
    // is computed and then thrown away in the grown branch (`dVar12` is
    // reassigned at :145). The port used to anchor the head instead, which put
    // the bus up to 0.11 units off across the 40 levels — small, but it is the
    // wrong end: the tail is what the handover test just cleared, so anchoring it
    // keeps the body exactly where the merge curve had it and lets the nose fall
    // where the road says.
    const tailPos = path.positionAt(Math.max(0, tail), _scratch)
    const tailRoadDistance = this.spline.distanceAt(this.spline.project(tailPos))
    this.headDistance = this.travelForwardWrapped(tailRoadDistance, this.bodyLength)
    this.entryPath = null
    // ❓ PLACEHOLDER. The handover this port takes is the `_isFullyGrown` arm of
    // `TransitionToMainRoad` — it rebuilds the head a full `bodyLength` ahead of
    // the projected tail, which *is* a grown bus. The `else` arm, which would
    // land a still-growing one, is **not ported**: A5 measured `_isFullyGrown`
    // false at 0 of 662 handovers, so it has never been reachable here.
    //
    // Setting the flag is what makes that consistent. Without it a bus that
    // merged before growing out would keep `isFullyGrown === false` for ever —
    // the entry path is gone, so nothing can advance the arc that flips it —
    // and `grownBodyLength` would compare a road distance against a body length
    // and clamp the drawn span short. To settle whether the `else` arm needs
    // porting: a level whose merge curve is shorter than a Large bus.
    //
    // Defensive, and measured to be so: growth always finishes before the
    // handover on shipped data, so deleting this line turns no test red. Kept
    // because the invariant it states — on the road means grown — is what the
    // rest of the class assumes.
    this.isFullyGrown = true
    this.state = 'OnMainRoad'
  }

  /** World pose while merging. Unity space, like the spline. */
  entryPose(out = { position: new Vector3(), tangent: new Vector3() }): typeof out {
    const path = this.entryPath!
    // Draw from the body centre, as the road case does.
    const centre = Math.max(0, this.headDistance - this.bodyLength / 2)
    path.positionAt(centre, out.position)
    path.tangentAt(centre, out.tangent)
    return out
  }

  private get spline(): DreamteckSpline {
    return this.road.spline
  }

  /** Total drivable length. The loop wraps over the whole spline. */
  get loopLength(): number {
    return this.spline.length
  }

  /**
   * Nose position expressed on the road, whichever curve the bus is actually
   * driving.
   *
   * While merging, distance past the join equals distance along the road, so a
   * bus still on the bridge maps to a point just *before* the entry — which is
   * what traffic and stop tests want: it is about to be there.
   */
  get roadHeadDistance(): number {
    // Distances are along the exit curve now. Its road section *is* the road,
    // so progress along it maps one-to-one back onto road distance for as long
    // as the body is still over the tarmac; past that the answer stops being
    // meaningful, but `isClearOfRoad` has excluded this bus by then.
    const exit = this.exitPath
    if (this.state === 'Exiting' && exit) {
      return this.wrap(this.roadDistanceAtDepart + (this.headDistance - exit.roadSectionLength))
    }

    const path = this.entryPath
    if (this.state === 'Entering' && path) {
      // Not clamped at the join. On a loop, "5 units before the entry" *is*
      // 5 units back around the far end, and wrapping says exactly that.
      // Clamping instead parks the merging bus on the join and blocks whoever
      // is coming through — measurably worse.
      return this.wrap(path.roadJoinDistance + (this.headDistance - path.joinDistance))
    }
    return this.headDistance
  }

  get tailDistance(): number {
    // While the bus is still growing out of the entry the tail is pinned at the
    // curve's start, so the span is whatever has emerged — not `bodyLength`.
    return this.wrap(this.roadHeadDistance - this.grownBodyLength)
  }

  /**
   * `BusSplineFollower.UpdateInvisibility` — is the bus off the drawn road?
   *
   * The loop the buses drive is longer than the road that is drawn for it. The
   * spline runs the whole 0..1; only `OriginalStartPercent..OriginalEndPercent`
   * gets a road swept onto it. The stretch between the two ends — across the
   * seam — is the gap the two `ConveyorCover` tunnel mouths cap, and a bus in
   * there is driving on nothing.
   *
   * The shipped game does not clip it. `UpdateInvisibility` moves the bus's
   * renderers, and every rider on it, to the `Invisible` layer (7 in
   * `TagManager`), turns the trails off, and reverses all of that on the way
   * back out. So the bus is simply not drawn while it is in the gap, and what
   * sells it as "driving into a tunnel" is that the cover is a solid opaque box
   * six units tall standing over the mouth: the bus slides in, the cover
   * occludes it, and the switch-off happens out of sight behind it.
   *
   * The margin is what buys that. Each end of the body is grown by its
   * passenger extension **plus 2.5 units** before the test, so by the time the
   * bus counts as outside, its visible extent is already well under the cover:
   *
   *     headReach = roadHeadDistance + passengerHeadExtension + 2.5
   *     tailReach = tailDistance     - passengerTailExtension - 2.5
   *     hidden    = headReach or tailReach lies outside the drawn arc
   *
   * `passengerHeadExtension` / `passengerTailExtension` are `Bus` 0xD4 / 0xD8 —
   * confirmed by their own getters — so this is per prefab, like everything
   * else that reads them.
   *
   * The two ends are tested against the arc rather than compared numerically:
   * the span wraps, and a bus straddling the seam has `tailPercent` numerically
   * *above* `headPercent`. The original branches on exactly that case; going
   * through arc offsets makes the wrap a non-event.
   *
   * What is **not** recoverable is the rest of the presentation. The covers'
   * `Cube` child carries `Invisible2`, a `RuffGames/invisible` material whose
   * only property is `_StencilID: 3` (the elevator's `Invisible` is 2), and the
   * URP renderer feature that consumes those ids is a MonoBehaviour that IL2CPP
   * stripped to an empty shell — `URP_Renderer.asset` and `RenderObjects.asset`
   * both come out with nothing but a name. So whether the shipped build also
   * stencil-clips the bus at the mouth, on top of switching it off, cannot be
   * read out of the kit. Switching it off is what is recoverable, and with the
   * cover standing over the mouth the two are hard to tell apart.
   *
   * ## Which mouths, not how near one
   *
   * The port cuts rather than switches off, and a cut needs to name the mouth
   * doing the cutting: each clip plane is infinite, and the loop bends back
   * under both of them — on level 6 and level 29 *every* metre of drawn road
   * lies past one plane or the other. Answering "is a mouth within 24 units"
   * armed buses standing on those stretches and sliced them in half, or made
   * them vanish outright, tens of metres from any tunnel.
   *
   * This answer is the arc question the original asks, and only the arc
   * question: does the padded body reach into the stub past the start, into the
   * stub past the end, or into both — the last being what a bus longer than the
   * 19.3-unit gap does while it is straddling the seam.
   *
   * Both stubs are straight, exactly 9.67 units, and measured across the forty
   * levels the road never crosses a mouth's own plane within 38 arc-units of
   * it — further than the longest body in the game. So a bus is only ever shown
   * the plane of a mouth it is genuinely standing in, and that plane cuts
   * nothing but the part of it that is in there. `npm run check:tunnel` holds
   * both halves of that.
   *
   * @param headMargin extra reach ahead of the nose — the original's
   *   `passengerHeadExtension + 2.5`
   * @param tailMargin the same behind the tail
   * @returns `ROAD_END_START | ROAD_END_END`
   */
  roadEndsReached(headMargin = 0, tailMargin = 0): number {
    return this.reachInto(headMargin, tailMargin)
  }

  /**
   * The same question with `UpdateInvisibility`'s **own** padding filled in.
   *
   * 🔍 `__0x2110330.c`:
   *
   *     head += (_bus.passengerHeadExtension + 2.5f) / _cachedMainSplineLength;
   *     tail -= (_bus.passengerTailExtension + 2.5f) / _cachedMainSplineLength;
   *
   * Two separate fields (0xD4 / 0xD8), so the two ends are padded by different
   * amounts, and a bare literal `2.5f` — the same *number* as `flowSpaceMargin`
   * (`SplineController.ctor` 0x3C) but not the same constant; nothing reads that
   * field here.
   *
   * This lives on the follower rather than in the renderer because it is read
   * out of the decomp, not chosen for how it looks — the view's job is only to
   * decide what to do with the answer.
   */
  reachesRoadEndPadded(tuning: PassengerTuning): number {
    return this.reachInto(
      tuning.passengerHeadExtension[this.size] + INVISIBILITY_PAD,
      tuning.passengerTailExtension[this.size] + INVISIBILITY_PAD,
    )
  }

  private reachInto(headMargin: number, tailMargin: number): number {
    // A bus merging out of the lot is on its own bridge, and one that has been
    // sent away is on its exit curve. Neither one's arc distance means anything
    // on the road, and neither is in a tunnel.
    if (this.state !== 'OnMainRoad' || this.isDeparting) return 0

    const length = this.loopLength
    if (length <= 0) return 0

    // 🔍 `UpdateInvisibility`:88-137, transcribed:
    //
    //     double head = _headPercent + (headExt + 2.5f) / _cachedMainSplineLength;
    //     double tail = _tailPercent;
    //     if (tail <= head) {
    //         tail -= (tailExt + 2.5f) / _cachedMainSplineLength;
    //         hide = !(head <= OriginalEndPercent) || tail < OriginalStartPercent;
    //     } else {
    //         hide = head < OriginalStartPercent;
    //     }
    //
    // `head` is deliberately **not** wrapped after the pad goes on: a nose
    // within the pad of the wrap point gives `head` past the end of the road
    // and hides, which is the right answer arrived at without a wrap. Both
    // sides below are unwrapped for the same reason.
    //
    // ❓ **Arc distance here, percent there, and the difference is not
    // recoverable.** The original divides a world length by the spline's total
    // length and adds the quotient to a *percent*, which is only the same thing
    // as advancing `ext + 2.5` world units when percent is uniform in arc
    // length — and Dreamteck's is not, which is why this port carries an
    // arc-length LUT at all. Doing the sum in percent instead moves the arming
    // point by about **0.04 units** on level 3, half a percent of the pad.
    // Distance is kept because it is the reading three existing tests already
    // measure to the millimetre, and because the port's percent curve is its
    // own reconstruction of Dreamteck's — trading a tested reading for an
    // untested one to chase 4 cm is a bad swap. Logged in checklist §10.
    const start = this.drawnStartDistance()
    const end = this.drawnEndDistance()
    const head = this.roadHeadDistance + headMargin
    const tail = this.tailDistance

    if (tail <= head) {
      // The ordinary case: the body does not straddle the seam, so each end is
      // judged against the end of the road nearest it. This is where the port's
      // extra bit of information comes from — the original only needs "hide",
      // the port needs to know *which* plane to cut against.
      let mask = 0
      if (!(head <= end)) mask |= ROAD_END_END
      if (tail - tailMargin < start) mask |= ROAD_END_START
      return mask
    }

    // 🔴 **The straddling case, and the tail plays no part in it.** Once the
    // nose has wrapped past the seam the original asks one question — has the
    // nose cleared `OriginalStartPercent`? — and nothing at all about where the
    // tail is. So a bus whose nose is back on tarmac is drawn **whole**, with
    // its rear still hanging over the undrawn stretch; the `ConveyorCover`
    // standing over the mouth is what hides that rear, not the renderer.
    //
    // This port used to answer the straddling case by testing the padded body
    // against both stubs, which set both bits and cut the bus at both mouths.
    // Measured over ten levels and 23 387 bus-frames, that disagreed with the
    // decompile on **820** of them — 804 here, all of them "the original draws
    // it, the port slices it" — and what the player saw was a bus coming out of
    // a tunnel with its back end chopped off for about a second. That is the
    // tunnel bug. `tests/tunnelVisibility.test.ts` counts the disagreements.
    return head < start ? ROAD_END_START | ROAD_END_END : 0
  }

  /** `OriginalStartPercent` as an arc distance. The road never changes shape. */
  private drawnStartDistance(): number {
    if (this.drawnStart < 0) this.drawnStart = this.spline.distanceAt(this.road.originalStartPercent)
    return this.drawnStart
  }

  /** `OriginalEndPercent`, likewise. */
  private drawnEndDistance(): number {
    if (this.drawnEnd < 0) this.drawnEnd = this.spline.distanceAt(this.road.originalEndPercent)
    return this.drawnEnd
  }

  get headPercent(): number {
    return this.spline.percentAtDistance(this.roadHeadDistance)
  }

  /**
   * `MainSplineHeadPercent` — the nose as a fraction of the loop, which is what
   * `SplineController.Update` sorts the ring by and measures gaps in.
   *
   * Distinct from `headPercent`: while a bus is `Entering` the original clamps
   * the reported nose to the join (see `mainSplineHeadDistance`). The spacing
   * loop only ever runs with every bus `OnMainRoad`, where the two agree — but
   * naming it after the original's getter keeps the parity test honest.
   */
  get mainSplineHeadPercent(): number {
    return this.spline.percentAtDistance(this.mainSplineHeadDistance)
  }

  get tailPercent(): number {
    return this.spline.percentAtDistance(this.tailDistance)
  }

  private wrap(distance: number): number {
    const length = this.loopLength
    if (length <= 0) return 0
    let d = distance % length
    if (d < 0) d += length
    return d
  }

  /** `TravelForwardWrapped` — run off the end and come back round the front. */
  travelForwardWrapped(fromDistance: number, distance: number): number {
    return this.wrap(fromDistance + distance)
  }

  /**
   * `CoversMainSplinePercent` — does the body span this percent?
   *
   * Has to handle the span straddling the wrap point, which happens every time
   * a bus is halfway through the tunnel.
   */
  coversPercent(percent: number): boolean {
    // `if (_state != OnMainRoad) return false` — the first line of the
    // original, and the *whole* of its guard. A bus part-way through its merge
    // is not at any stop even once its nose has reached the road, and one
    // running out has already been moved to `Exiting` by `DepartAndExit`.
    //
    // This used to also test `isDeparting`, which the original does not: that
    // filter lives in `QueueController.FindBusAtStop`, one level up. The extra
    // condition was unobservable — `departAndExit` sets the flag and the state
    // together — but it hid which gate was doing the work, so it is gone.
    if (this.state !== 'OnMainRoad') return false
    const target = this.spline.distanceAt(percent)
    const head = this.roadHeadDistance
    const tail = this.tailDistance
    if (tail <= head) return target >= tail && target <= head
    // Wrapped: the body runs off the end and continues from the start.
    return target >= tail || target <= head
  }

  /**
   * Clear road ahead of this bus's nose before it reaches `other`'s tail,
   * measured forward around the loop.
   *
   * Everything is one-directional, so "ahead" is always the forward wrap — a
   * bus just in front returns a small value, and one that is nearly a full lap
   * ahead returns nearly the loop length.
   */
  gapAheadOf(other: BusSplineFollower): number {
    return this.wrap(other.trafficTailDistance - this.mainSplineHeadDistance)
  }

  /**
   * The first test in `ComputeTrafficSpeedFactor`: is my nose inside the
   * stretch of road `other` claims? If it is, the factor is 0 and the loop
   * stops — no ramp, no distance, a dead stop.
   *
   *     double a = other.State == OnMainRoad ? other._tailPercent
   *                                          : other.MainSplineEntryPercent;
   *     double b = other.MainSplineHeadPercent;
   *     if (a <= b) { if (a <= mine && mine <= b) return 0f; }
   *     else        { if (mine >= a || mine <= b) return 0f; }
   *
   * The two branches are the unwrapped and wrapped spans; walking both as an
   * offset from the span start collapses them into one comparison.
   */
  occupiedBy(other: BusSplineFollower): boolean {
    const tail = other.trafficTailDistance
    return (
      this.wrap(this.mainSplineHeadDistance - tail) <=
      this.wrap(other.mainSplineHeadDistance - tail)
    )
  }

  /**
   * `MainSplineHeadPercent` (0x210cbe0) as an arc distance — where this bus
   * counts as being **on the road**, whatever curve it is really driving.
   *
   *     return State == Entering ? _cachedMainSplineHeadPercent : _headPercent;
   *
   * and `LateUpdate` recomputes the cache every frame a bus is merging
   * (`__0x210cc5c.c`, the block right before `ComputeTrafficSpeedFactor`):
   *
   *     double join = _currentSpline.GetPointPercent(_entryPointIndex);
   *     _cachedMainSplineHeadPercent =
   *         _headPercent <= join
   *             ? MainSplineEntryPercent                       // clamped
   *             : wrap01(MainSplineEntryPercent
   *                      + _currentSpline.CalculateLength(...) / _cachedMainSplineLength);
   *
   * **The clamp is the whole point.** Until its nose reaches the join, a merging
   * bus reports the join itself, so its claimed span is a single point sitting on
   * the road — not a span drifting backwards from it. `roadHeadDistance` does
   * drift backwards (deliberately: it answers "how far before the entry am I"),
   * which is why traffic cannot use that one.
   */
  get mainSplineHeadDistance(): number {
    const path = this.entryPath
    if (this.state === 'Entering' && path) {
      return this.wrap(path.roadJoinDistance + Math.max(0, this.headDistance - path.joinDistance))
    }
    return this.roadHeadDistance
  }

  /**
   * The back edge of the stretch this bus claims, as everybody behind it
   * measures their gap to.
   *
   *     other.State == OnMainRoad ? other._tailPercent : other.MainSplineEntryPercent
   *
   * — the selector inside `ComputeTrafficSpeedFactor`'s loop. A bus that is
   * merging claims from **the join** forward, not from its own tail: the whole
   * reason road traffic stops and waits for it. Its tail is out on the entry
   * curve and means nothing on the road.
   *
   * ' + B + 'Exiting' + B + ' is this port's name for ' + B + 'OnMainRoad && IsDeparting' + B + ' — the
   * original never leaves `OnMainRoad` on the way out (`DepartAndExit` does
   * not touch 0x3C) — so it takes the tail branch, like the state it really is.
   */
  get trafficTailDistance(): number {
    const path = this.entryPath
    if (this.state === 'Entering' && path) return this.wrap(path.roadJoinDistance)
    return this.tailDistance
  }

  /** Advance along the road. While exiting the bus stops wrapping. */
  advance(distance: number): void {
    if (distance <= 0) return
    if (this.state === 'Entering' && this.entryPath) {
      this.headDistance = Math.min(this.entryPath.length, this.headDistance + distance)
      // `LateUpdate`: once the arc from the curve's start to the nose covers a
      // body length, the whole bus is out and the tail starts chasing.
      if (!this.isFullyGrown && this.drawnMiddleSpan >= this.growLength) this.isFullyGrown = true
      this.checkStateTransition()
      return
    }
    if (this.isDeparting) {
      this.exitDistance += distance
      const path = this.exitPath
      // On the exit curve distances are measured along it, not the road, and
      // it does not wrap — the bus drives off the end and is removed.
      this.headDistance = Math.min(
        path ? path.length : this.loopLength,
        this.headDistance + distance,
      )
      return
    }
    this.headDistance = this.travelForwardWrapped(this.headDistance, distance)
  }

  /**
   * Port of `BusSplineFollower.GetPassengerSlotPose`.
   *
   *     rows       = max(1, (int)(capacity / 3))
   *     rowSpacing = (tailExt + headExt + busLength) / rows
   *     along      = ((slot / 3) + 0.5) * rowSpacing
   *     if (slot % 3 == 1) along += rowSpacing * -0.25      // middle column back
   *     along     -= headExt
   *     percent    = along >= 0 ? TravelBackwardWrapped(head, along)
   *                             : TravelForwardWrapped(head, -along)
   *     lateral    = passengerSideOffset * ((slot % 3) - 1) // -1, 0, +1
   *
   * Passengers ride three abreast on the roof — the same width-3 grid the
   * queues use, and the reason capacities are 18 / 36 / 54 (6 / 12 / 18 rows).
   *
   * The seat is measured back from the head along **`_currentSpline`** — the
   * curve the bus is actually on, not the road. That distinction is the whole
   * point of returning a pose here rather than a road distance: while a bus is
   * merging or running out it is off the road entirely, and passengers computed
   * against the road stay behind on the tarmac while the bus drives away.
   */
  passengerSlotPose(
    slotIndex: number,
    capacity: number,
    tuning: PassengerTuning,
    out = { position: new Vector3(), tangent: new Vector3(), lateral: 0, along: 0 },
  ): typeof out {
    const rows = Math.max(1, Math.trunc(Math.max(2, capacity) / 3))
    // Both come off *this* bus in the original — `_bus.passengerHeadExtension`
    // — so a small bus and a large one on the same road use different numbers.
    const headExt = tuning.passengerHeadExtension[this.size]
    const tailExt = tuning.passengerTailExtension[this.size]
    const rowSpacing = (tailExt + headExt + this.bodyLength) / rows

    const column = ((slotIndex % 3) + 3) % 3
    let along = (Math.floor(slotIndex / 3) + 0.5) * rowSpacing
    if (column === 1) along += rowSpacing * -0.25
    along -= headExt

    out.lateral = tuning.passengerSideOffset * (column - 1)
    // Handed back as well as used: the renderer drops a rider whose seat has
    // gone past the end of the tarmac, and it has to measure that with the
    // same offset the seat was placed at rather than a second guess at it.
    out.along = along
    this.bodyPose(along, out, true)
    return out
  }

  /**
   * `UpdatePassengerSwayOnly` — the one heading a **stopped** bus hands to its
   * whole busload.
   *
   * 🔍 `BusSplineFollower$LateUpdate__0x210cc5c.c` (the method is inlined at
   * line 104 and called again at 274):
   *
   *     if (_passengers.Count == 0) return;
   *     Vector3 f = _samplesValid ? _cachedHeadSample.forward
   *                               : _currentSpline.Evaluate(_headPercent).forward;
   *     Vector3 dir = new Vector3(f.x, 0f, f.z);
   *     Quaternion rot = dir.sqrMagnitude <= 0.001f
   *                        ? Quaternion.identity
   *                        : Quaternion.LookRotation(dir.normalized, Vector3.up);
   *     foreach (var c in _passengers) c.transform.rotation = rot * c.SwayRotation;
   *
   * Two things to keep straight:
   *
   *  - The sample is `_cachedHeadSample`, written by `ApplyPositions` as
   *    `Evaluate(_headPercent)` — the **nose**. A stopped bus squares its whole
   *    busload to the curve under its front bumper, so on a bend the ones at
   *    the back are turned away from the road they are actually standing over.
   *  - Returning `(0, 0, 1)` for the degenerate case is not a made-up fallback:
   *    `Quaternion.identity` is exactly `LookRotation(Vector3.forward, up)`.
   */
  swayHeading(out = new Vector3()): Vector3 {
    this.bodyPose(0, _swayPose, true)
    out.copy(_swayPose.tangent)
    out.y = 0
    // sqrMagnitude of the **raw** (x, 0, z), before normalising — the original
    // tests it that way, and `forward` is already unit length, so this only
    // fires where the curve is close to vertical.
    if (out.lengthSq() <= 0.001) return out.set(0, 0, 1)
    return out.normalize()
  }

  /**
   * Pose on `_currentSpline` at a point `along` units behind the bus's head.
   *
   * The one place that answers "where is this part of the bus right now",
   * whichever curve it happens to be driving. Seats use it, and so does the
   * body mesh when it bends along the road.
   */
  bodyPose(
    along: number,
    out: { position: Vector3; tangent: Vector3 } = {
      position: new Vector3(),
      tangent: new Vector3(),
    },
    /**
     * Stop at the ends of the spline instead of wrapping round to the other one.
     *
     * **Anything that draws geometry has to pass this.** The loop is driven as a
     * ring, but the spline is *open* — `SplineIsClosed` is false on all forty
     * levels — and its two ends are nowhere near each other: 7.9 units apart on
     * level 4, 76 on level 12, 79 on level 20. Wrapping is right for the
     * arithmetic, where a distance is a distance; it is catastrophic for a mesh,
     * because two consecutive samples either side of the seam land at opposite
     * corners of the level and every triangle between them is smeared across
     * the whole map. That is the black-and-red streak the port drew whenever a
     * bus straddled a tunnel.
     *
     * The shipped body has no such seam to cross: it is a Dreamteck
     * `SplineMesh` bounded by `clipFrom` / `clipTo`, which generate nothing
     * outside [0, 1], plus the two cap bodies `InitCapBodies` puts on the same
     * spline. Clamping is that bound. Whatever piles up at the clamp sits past
     * `OriginalEndPercent` — off the drawn road, inside a tunnel — where
     * `RoadClip` discards it.
     */
    clampToSpline = false,
    /**
     * Which turn of the loop to measure this point on, in road distance.
     *
     * 🔍 The two segments of `BusBodyMesh.RebuildImmediate` (`__0x20fec80.c`
     * :207-262): a clip window with `clipFrom > clipTo` is drawn as
     * `(clipFrom, 1)` **and** `(0, clipTo)`, two pieces, one at each end of the
     * spline. `branch = 0` is the second; `branch = loopLength` shifts the whole
     * body a lap forward so the clamp keeps the *first* instead. Both are
     * clamped, so neither ever crosses the seam and neither can smear.
     *
     * Only the drawing code passes this, and only when
     * `BusFleet.seamBranch` says the body reaches past an end of the road.
     */
    branch = 0,
  ): typeof out {
    const path = this.currentPath
    if (path) {
      const distance = Math.max(0, Math.min(path.length, this.headDistance - along))
      path.positionAt(distance, out.position)
      path.tangentAt(distance, out.tangent)
      return out
    }

    const raw = this.roadHeadDistance - along + branch
    const distance = clampToSpline
      ? Math.max(0, Math.min(this.loopLength, raw))
      : this.wrap(raw)
    const percent = this.spline.percentAtDistance(distance)
    this.spline.evaluatePosition(percent, out.position)
    this.spline.evaluateTangent(percent, out.tangent)
    return out
  }

  /**
   * `_cachedCurrentSplineLength` (0xA4) — the world length of whichever curve
   * this bus is on, entry or exit or road.
   *
   * `UpdateClearanceProbes` divides its two cap lengths by this to turn them
   * into percent, and returns without touching anything when it is under 1.
   */
  get cachedCurrentSplineLength(): number {
    return this.currentPath?.length ?? this.spline.length
  }

  /**
   * True while `_currentSpline` **is** the road, so distances wrap round the
   * loop and the body can straddle the seam. False on the entry and exit
   * curves, which have real ends and no wrap.
   */
  get ridesMainSpline(): boolean {
    return this.currentPath === null
  }

  /** `_currentSpline`, when it is not the main road. */
  private get currentPath(): SampledPath | null {
    if (this.exitPath) return this.exitPath
    return this.isOnEntryPath ? this.entryPath : null
  }

  /**
   * `DepartAndExit` — leave the loop.
   *
   *     IsHeldAtStop = false; IsDeparting = true      (the 0x100 store at 0x48)
   *     publish BusDepartedEvent
   *     exit = CreateExitSpline(...)
   *     _currentSpline = exit
   *     _tailPercent = Travel(0, headExtension + minGap + 2)
   *     _headPercent = Travel(_tailPercent, bodyLengthPercent)
   *
   * The rear lands `leadIn` along the new curve and the nose a body-length
   * past it — the same orientation the bus had on the road, not a swap.
   *
   * The bus swaps onto the exit spline outright — it is no longer on the road
   * from this point, which is why `IsSpaceClearForBus` is free to ignore it.
   */
  departAndExit(exitPath: ExitPath): void {
    if (this.isDeparting) return
    this.roadDistanceAtDepart = this.roadHeadDistance
    this.isDeparting = true
    this.isHeldAtStop = false
    this.exitPath = exitPath
    this.state = 'Exiting'
    this.entryPath = null
    // The curve's road section ends where the bus's head is, so that is where
    // the head starts on it — the body then covers the section behind.
    this.headDistance = Math.min(exitPath.roadSectionLength, exitPath.length)
    this.exitDistance = 0
  }

  /** World pose while running out. Unity space, like the spline. */
  exitPose(out = { position: new Vector3(), tangent: new Vector3() }): typeof out {
    const path = this.exitPath!
    const centre = Math.max(0, this.headDistance - this.bodyLength / 2)
    path.positionAt(centre, out.position)
    path.tangentAt(centre, out.tangent)
    return out
  }

  /**
   * Has the body finished leaving the tarmac?
   *
   * ⚠️ **This has no counterpart in the original, and no longer decides
   * anything.** The comment that stood here said "traffic and merge checks have
   * to keep seeing it", and both halves are now false:
   *
   *  - `IsSpaceClearForBus` stopped using it at PS-2,
   *  - `ComputeTrafficSpeedFactor` stopped using it at PS-17.
   *
   * Both were corrected the same way and for the same reason: `DepartBus`
   * (`__0x212385c.c`:60) calls `RemoveActiveBus` **before** the inlined
   * `DepartAndExit` at `:94` sets `IsDeparting`, so a bus is out of
   * `_activeSplineBuses` from the instant it is released, and every loop over
   * that set stops seeing it there and then — body still on the road or not.
   *
   * Nor does anything else in the decompile ask the question. `LateUpdate`'s
   * departing branch (`__0x210cc5c.c`:59-97) runs `_headPercent` forward at
   * `_worldSpeed * 1.68 / _cachedCurrentSplineLength` and, at **0.999**, sets
   * the completed latch and calls `Bus.CompleteOffscreen`. That is the whole
   * lifecycle: no road-section boundary is read anywhere in it.
   *
   * It is kept because four test files need a **name** for the one state the
   * two readings disagree about — departing, but not yet a body-length past the
   * road section — and `trafficFilterFixture.test.ts` builds exactly that state
   * to hold PS-17 in place. Deleting the getter would scatter the same
   * expression across all of them. If those tests ever go, this goes with them.
   */
  get isClearOfRoad(): boolean {
    const path = this.exitPath
    if (!this.isDeparting) return false
    if (!path) return true
    return this.headDistance - path.roadSectionLength >= this.bodyLength
  }

  /**
   * Fully clear of the road, ready to be removed.
   *
   * 🔍 `LateUpdate__0x210cc5c.c`:70 — `if (0.999 <= _headPercent)`, and then the
   * completed latch (0x80) plus `Bus.CompleteOffscreen`. The threshold is a
   * **fraction of the exit spline**, so it is written as one here.
   *
   * It used to read `headDistance >= path.length - 1e-3`, a distance a
   * thousandth short of the end. The two agree only on a one-unit curve; on a
   * curve of length L they differ by `0.001 * (L - 1)`. Measured over 40 levels,
   * **0 of 515** departing buses had the two fire on different frames — the exit
   * curves run 40.8 to 68.7 units, so the gap is 0.04 to 0.07 units against a
   * 0.75-unit step (`26.88 * 1.68 / 60`) and both are always crossed together.
   * Changed anyway, because the original's literal is readable and a port that
   * writes something else is one more thing to re-derive later.
   *
   * ❓ **One difference deliberately left standing.** The original advances
   * `_headPercent` and *then* tests it; `advanceRoadTraffic` tests this and then
   * advances. So the port drops the follower one frame later. Of the loops over
   * `followers`, every one skips departing buses except the fail check's step 5,
   * which can therefore be suppressed for a single frame — and since that check
   * re-runs every frame, a one-frame delay cannot change the outcome. Moving the
   * test would be a control-flow change bought for nothing measurable.
   */
  get hasLeft(): boolean {
    if (!this.isDeparting) return false
    const path = this.exitPath
    if (path) return this.headDistance >= EXIT_COMPLETE_PERCENT * path.length
    return this.exitDistance > this.bodyLength + EXIT_CLEARANCE
  }
}

/**
 * Extra run-out before a departing bus is removed. The original builds a real
 * exit spline extended by `tailCapWorldLength + PassengerTailExtension + 2.0`;
 * with a straight run-out the constant is all that survives.
 */
const EXIT_CLEARANCE = 2.0

/**
 * 🔍 Where the exit run ends — `LateUpdate__0x210cc5c.c`:70, `0.999 <=
 * _headPercent`, immediately before the completed latch and
 * `Bus.CompleteOffscreen`.
 */
const EXIT_COMPLETE_PERCENT = 0.999

const _scratch = new Vector3()
const _swayPose = { position: new Vector3(), tangent: new Vector3() }

/**
 * 🔍 The literal `2.5f` `UpdateInvisibility` adds to each passenger extension
 * before asking whether the bus has left the drawn road.
 */
export const INVISIBILITY_PAD = 2.5
